import { api } from './client';
import type {
  User,
  AuthResponse,
  SignupRequest,
  PasswordLoginRequest,
  PasswordResetConfirm,
  University,
  Course,
  CourseSummary,
  CoursesResponse,
  CourseSearchParams,
  TrackedProgram,
  TrackerStats,
  DeadlineItem,
  ReminderItem,
  PrintablePlan,
  GhadamTransaction,
  CreateTrackedProgramRequest,
  UpdateTrackedProgramRequest,
  UserGoal,
  ApplicationStatus,
  Priority,
  MatchingProfile,
  MatchingOptions,
  RecommendationsResponse,
  ChecklistItem,
  NoteEntry,
  CourseLanguageRequirement,
  PublicExperience,
  ExperienceRecord,
  ExperienceSubmission,
  ExperienceModerationStatus,
  SourcesResponse,
  ApplicationCosts,
  CurrencyRate,
  CommunityReport,
  CommunityReportAdmin,
  ReportModeration,
  ReportType,
  TestSession,
  PathDiagnosis,
  PathProfile,
  PathProfileResponse,
} from '../types';

// Auth
export const authApi = {
  signup: (data: SignupRequest) =>
    api.post<AuthResponse>('/auth/signup', data),

  loginWithPassword: (data: PasswordLoginRequest) =>
    api.post<AuthResponse>('/auth/login', data),

  requestPasswordReset: (email: string) =>
    api.post<{ message: string }>('/auth/password-reset/request', { email }),

  confirmPasswordReset: (data: PasswordResetConfirm) =>
    api.post<AuthResponse>('/auth/password-reset/confirm', data),

  getMe: () => api.get<User>('/auth/me'),

  updateMe: (data: Partial<User>) => api.patch<User>('/auth/me', data),

  exportMyData: () => api.get<Record<string, unknown>>('/auth/me/export'),

  deleteAccount: () =>
    api.delete<void>('/auth/me', { body: JSON.stringify({ confirm: 'DELETE' }) }),

  setGoal: (goal: UserGoal) => api.post<User>('/auth/onboarding', { goal }),

  completeOnboarding: () => api.post<User>('/auth/onboarding/complete'),
};

// Matching
export const matchingApi = {
  getOptions: () => api.get<MatchingOptions>('/matching/options'),
  
  getProfile: () => api.get<{ profile: MatchingProfile; completed: boolean }>('/matching/profile'),
  
  saveProfile: (profile: MatchingProfile) =>
    api.post<{
      profile: MatchingProfile;
      completed: boolean;
      bonus_awarded: number;
      new_balance: number;
    }>('/matching/profile', profile),
  
  getRecommendations: (params?: { limit?: number; offset?: number; min_score?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    if (params?.min_score) searchParams.set('min_score', String(params.min_score));
    const query = searchParams.toString();
    return api.get<RecommendationsResponse>(`/matching/recommendations${query ? `?${query}` : ''}`);
  },
  
  getQuickRecommendations: (profile: Partial<MatchingProfile>) =>
    api.post<{ recommendations: Array<{ id: number; program_name: string; university_name: string; country: string; match_score: number; match_reasons: string[] }>; message: string }>(
      '/matching/quick-recommendations',
      profile
    ),
  
  trackRecommendation: (courseId: number, priority: Priority = 'target', intake?: string) =>
    api.post<{ id: number; course_id: number; match_score: number; match_reasons?: string[]; warnings?: string[]; recommendation_snapshot?: Record<string, unknown>; message: string }>(
      `/matching/recommendations/${courseId}/track`,
      { priority, intake }
    ),
};

// Tracker
export const trackerApi = {
  listPrograms: (status?: ApplicationStatus, priority?: Priority) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (priority) params.set('priority', priority);
    const query = params.toString();
    return api.get<TrackedProgram[]>(`/tracker/programs${query ? `?${query}` : ''}`);
  },
  
  getProgram: (id: number) => api.get<TrackedProgram>(`/tracker/programs/${id}`),
  
  addProgram: (data: CreateTrackedProgramRequest) =>
    api.post<TrackedProgram>('/tracker/programs', data),
  
  updateProgram: (id: number, data: UpdateTrackedProgramRequest) =>
    api.patch<TrackedProgram>(`/tracker/programs/${id}`, data),
  
  deleteProgram: (id: number) => api.delete<{ ok: boolean }>(`/tracker/programs/${id}`),
  
  getStats: () => api.get<TrackerStats>('/tracker/stats'),
  
  getDeadlines: (days = 90) => api.get<DeadlineItem[]>(`/tracker/deadlines?days=${days}`),

  getReminders: (days = 90) => api.get<ReminderItem[]>(`/tracker/reminders?days=${days}`),

  getPrintablePlan: (programId?: number) => {
    const query = programId ? `?program_id=${programId}` : '';
    return api.get<PrintablePlan>(`/tracker/export${query}`);
  },
  
  // Checklist
  updateChecklist: (id: number, checklist: ChecklistItem[]) =>
    api.patch<{ ok: boolean; checklist: ChecklistItem[] }>(`/tracker/programs/${id}/checklist`, checklist),
  
  addChecklistItem: async (id: number, item: { name: string; required?: boolean; notes?: string; due_date?: string }) => {
    const result = await api.post<{ ok: boolean; item: ChecklistItem; checklist: ChecklistItem[] }>(`/tracker/programs/${id}/checklist/items`, item);
    // Return a partial TrackedProgram with the updated checklist
    return { id, document_checklist: result.checklist } as TrackedProgram;
  },

  deleteChecklistItem: async (id: number, itemId: string) => {
    const result = await api.delete<{ ok: boolean; checklist: ChecklistItem[] }>(`/tracker/programs/${id}/checklist/items/${itemId}`);
    // Return a partial TrackedProgram with the updated checklist
    return { id, document_checklist: result.checklist } as TrackedProgram;
  },
  
  // Notes
  getNotes: (id: number) => api.get<{ main_notes: string | null; entries: NoteEntry[] }>(`/tracker/programs/${id}/notes`),
  
  updateMainNotes: (id: number, notes: string) =>
    api.patch<{ ok: boolean; notes: string }>(`/tracker/programs/${id}/notes`, { notes }),
  
  addNoteEntry: async (id: number, entry: { content: string; category: string; pinned?: boolean }) => {
    const result = await api.post<{ ok: boolean; entry: NoteEntry }>(`/tracker/programs/${id}/notes/entries`, entry);
    return result.entry;
  },
  
  updateNoteEntry: async (id: number, entryId: string, data: { content?: string; category?: string; pinned?: boolean }) => {
    const result = await api.patch<{ ok: boolean; entries: NoteEntry[] }>(`/tracker/programs/${id}/notes/entries/${entryId}`, data);
    return result.entries.find(e => e.id === entryId) as NoteEntry;
  },
  
  deleteNoteEntry: (id: number, entryId: string) =>
    api.delete<{ ok: boolean }>(`/tracker/programs/${id}/notes/entries/${entryId}`),

  getCosts: () => api.get<ApplicationCosts>('/tracker/costs'),
};

// Universities
export const universityApi = {
  list: (params?: { query?: string; country?: string; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.query) searchParams.set('query', params.query);
    if (params?.country) searchParams.set('country', params.country);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const query = searchParams.toString();
    return api.get<University[]>(`/universities${query ? `?${query}` : ''}`);
  },
  
  get: (id: number) => api.get<University>(`/universities/${id}`),
  
  getCountries: () => api.get<Array<{ country: string; count: number }>>('/universities/countries'),

  create: (data: { name: string; country: string; city: string }) =>
    api.post<University>('/universities', data),
  
  getCourses: (id: number) => api.get<Course[]>(`/universities/${id}/courses`),
};

// Courses
export const courseApi = {
  search: (params?: CourseSearchParams) => {
    const searchParams = new URLSearchParams();
    
    if (params?.query) searchParams.set('query', params.query);
    if (params?.field) searchParams.set('field', params.field);
    if (params?.degree_level) searchParams.set('degree_level', params.degree_level);
    if (params?.teaching_language) searchParams.set('teaching_language', params.teaching_language);
    if (params?.max_tuition !== undefined) searchParams.set('max_tuition', String(params.max_tuition));
    if (params?.tuition_free_only) searchParams.set('tuition_free_only', 'true');
    if (params?.low_barrier_only) searchParams.set('low_barrier_only', 'true');
    if (params?.deadline_after) searchParams.set('deadline_after', params.deadline_after);
    if (params?.deadline_before) searchParams.set('deadline_before', params.deadline_before);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    
    // Multi-select: countries
    if (params?.countries && params.countries.length > 0) {
      params.countries.forEach((c) => searchParams.append('countries', c));
    } else if (params?.country) {
      searchParams.set('country', params.country);
    }
    
    // Multi-select: fields
    if (params?.fields && params.fields.length > 0) {
      params.fields.forEach((f) => searchParams.append('fields', f));
    }
    
    const query = searchParams.toString();
    return api.get<CoursesResponse>(`/courses${query ? `?${query}` : ''}`);
  },
  
  autocomplete: (q: string) => api.get<CourseSummary[]>(`/courses/autocomplete?q=${encodeURIComponent(q)}`),
  
  get: (id: number) => api.get<Course>(`/courses/${id}`),

  getLanguageRequirements: (id: number) =>
    api.get<CourseLanguageRequirement[]>(`/courses/${id}/language-requirements`),
  
  getFields: (minCount = 5) => api.get<Array<{ field: string; count: number }>>(`/courses/fields?min_count=${minCount}`),
  
  getStats: (id: number) => api.get<{
    course_id: number;
    total_applications: number;
    accepted: number;
    rejected: number;
    acceptance_rate: number | null;
  }>(`/courses/${id}/stats`),
};

// Sources metrics
export const sourceApi = {
  list: () => api.get<SourcesResponse>('/sources'),
};

// Experiences
export const experiencesApi = {
  browse: (params?: {
    query?: string;
    country?: string;
    university_id?: number;
    course_id?: number;
    degree_level?: string;
    status?: string;
    scholarship_received?: boolean;
    skip?: number;
    limit?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.query) searchParams.set('query', params.query);
    if (params?.country) searchParams.set('country', params.country);
    if (params?.university_id) searchParams.set('university_id', String(params.university_id));
    if (params?.course_id) searchParams.set('course_id', String(params.course_id));
    if (params?.degree_level) searchParams.set('degree_level', params.degree_level);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.scholarship_received !== undefined) searchParams.set('scholarship_received', String(params.scholarship_received));
    if (params?.skip) searchParams.set('skip', String(params.skip));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const query = searchParams.toString();
    return api.get<PublicExperience[]>(`/experiences${query ? `?${query}` : ''}`);
  },

  get: (id: number) => api.get<PublicExperience>(`/experiences/${id}`),

  getMine: () => api.get<ExperienceRecord[]>('/experiences/my'),

  submit: (data: ExperienceSubmission) =>
    api.post<ExperienceRecord>('/experiences', data),

  submitFromTracker: (programId: number, data: ExperienceSubmission) =>
    api.post<ExperienceRecord>(`/experiences/from-tracker/${programId}`, data),

  getReviewQueue: (status: ExperienceModerationStatus = 'submitted') =>
    api.get<ExperienceRecord[]>(`/experiences/admin/review?moderation_status=${status}`),

  moderate: (id: number, data: { moderation_status: ExperienceModerationStatus; moderation_notes?: string }) =>
    api.patch<ExperienceRecord>(`/experiences/admin/${id}`, data),
};

// Currencies
export const currencyApi = {
  getRates: () => api.get<CurrencyRate[]>('/currencies/rates'),

  convert: (amount: number, from: string, to: string) =>
    api.get<{ amount: number; from: string; to: string; result: number }>(
      `/currencies/convert?amount=${amount}&from_currency=${from}&to_currency=${to}`
    ),
};

// Coins
export const ghadamApi = {
  getBalance: () => api.get<{ balance: number; user_id: number }>('/ghadam/balance'),
  
  getTransactions: (limit = 20, offset = 0) =>
    api.get<GhadamTransaction[]>(`/ghadam/transactions?limit=${limit}&offset=${offset}`),
  
  getPricing: () => api.get<{
    profile_view_cost: number;
    contributor_share: number;
    rewards: Record<string, number>;
  }>('/ghadam/pricing'),
};
// Community reports
export const communityReportsApi = {
  list: (params: { report_type: ReportType; subject_key?: string; subject_prefix?: string; include_stale?: boolean }) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)])
    );
    return api.get<CommunityReport[]>(`/community-reports?${query}`);
  },

  submit: (data: { report_type: ReportType; content: Record<string, unknown>; observed_at?: string }) =>
    api.post<CommunityReport>('/community-reports', data),

  mine: () => api.get<CommunityReport[]>('/community-reports/mine'),

  queue: (moderation_status: ReportModeration, report_type?: ReportType) =>
    api.get<CommunityReportAdmin[]>(
      `/community-reports/admin/queue?moderation_status=${moderation_status}${report_type ? `&report_type=${report_type}` : ''}`
    ),

  moderate: (id: number, data: { moderation_status: ReportModeration; verified?: boolean; moderation_notes?: string }) =>
    api.patch<CommunityReportAdmin>(`/community-reports/admin/${id}`, data),
};

// Official test sessions
export const testSessionsApi = {
  list: (params: { test_type?: string; city?: string }) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v).map(([k, v]) => [k, String(v)])
    );
    return api.get<TestSession[]>(`/test-sessions?${query}`);
  },

  cities: (test_type?: string) =>
    api.get<Array<{ city: string; sessions: number }>>(`/test-sessions/cities${test_type ? `?test_type=${test_type}` : ''}`),
};

// Personal path
export const pathApi = {
  getProfile: () => api.get<PathProfileResponse>('/path/profile'),
  saveProfile: (profile: PathProfile) => api.put<PathProfileResponse>('/path/profile', profile),
  current: () => api.get<PathDiagnosis>('/path/current'),
  recalculate: () => api.post<PathDiagnosis>('/path/recalculate'),
  diagnose: (profile: PathProfile) => api.post<PathDiagnosis>('/path/diagnose', profile),
  deleteProfile: () => api.delete<void>('/path/profile'),
};
